import { useMemo, useSyncExternalStore } from 'react';

import type { CrmObject } from '@/types/crm';

import {
  getObjectByPk,
  resolveEntityId,
  subscribeToStoredObject,
} from '../../store/crmObjectStore';

export interface StoreObjectSubscriptionResult {
  storeObjectData:
    | (CrmObject & { associated_objects?: Record<string, CrmObject> })
    | undefined;
  storeObjectIsComplete: boolean;
}

/**
 * Subscribes to the per-object CRM store entry for real-time WS updates.
 *
 * We subscribe to the StoredObject (data + meta) rather than the
 * complete-only `subscribeToObject` so the panel can render a placeholder
 * header from a *summary* entry (e.g. one upserted by the table's list
 * response) while the full fetch is in flight. This eliminates the
 * cold-fetch full-panel skeleton flash on first open of an entity that's
 * already known to the store.
 *
 * Resolves numeric PKs via getObjectByPk (numeric-PK opens are real — see
 * SlideoutStackContext.tsx pushSlideout(number)), then falls back to the
 * merge-alias map for UUID-format ids.
 */
export function useStoreObjectSubscription(
  objectId: string
): StoreObjectSubscriptionResult {
  // Resolve numeric PKs via getObjectByPk, then fall back to the
  // merge-alias map for UUID-format ids.
  const resolvedTimelineId =
    getObjectByPk(objectId)?.entity_id ?? resolveEntityId(objectId);
  const { subscribe: objSubscribe, getSnapshot: objGetSnapshot } = useMemo(
    () => subscribeToStoredObject(resolvedTimelineId),
    [resolvedTimelineId]
  );
  const storedObject = useSyncExternalStore(objSubscribe, objGetSnapshot);
  const storeObjectData = storedObject?.data;
  const storeObjectIsComplete = storedObject?.meta.isComplete ?? false;

  return useMemo(
    () => ({ storeObjectData, storeObjectIsComplete }),
    [storeObjectData, storeObjectIsComplete]
  );
}
